import { useEffect, useRef, useState } from "react";
import { Toaster } from "react-hot-toast";
import { Storage } from "react-jhipster";
import { connect } from "react-redux";
import { BrowserRouter } from "react-router-dom";
// socket.io-client v4: named export `io` (the v2 default-export form is gone).
// All methods we use (.on / .off / .emit / .disconnect / .connected) and
// constructor options (query, transports, reconnection*) are unchanged.
import { io } from "socket.io-client";
import { AUTH_TOKEN_KEY, LANGUAGE } from "./config/constants";
import AppLayout from "./container/app-layout";
import { notificationAlert } from "./helper/notifications";
import { getAdminManagerReports } from "./redux/reducers/admin-manager-reports/admin-manager-report.reducer";
import { getSession, clearAuthentication } from "./redux/reducers/authentication/authentication";
import {
  getAssignedFormsForUser,
  getForms,
} from "./redux/reducers/form/form.reducer";
import { getAllNotifications } from "./redux/reducers/notifications/notifications.reducer";
import { getSubscriptionByCompanyId } from "./redux/reducers/subscription/subscription.reducer";
import SubscriptionCustomToast from "./components/dialogs/SubscriptionCustomToast";
import { i18n } from "./pages/private/languageSelector";

const App = ({
  user,
  getSession,
  currentRole,
  currentRoute,
  getForms,
  getAllNotifications,
  getAdminManagerReports,
  getAssignedFormsForUser,
  getSubscriptionByCompanyId,
  clearAuthentication,
}) => {
  const [, setTheme] = useState("");
  const [socket, setSocket] = useState(null);
  const [shouldReload, setShouldReload] = useState(false);
  const [languageSelected, setLanguageSelected] = useState("ARABIC");

  // Use a ref for companyId so the payment listener always has the latest value
  // without needing to be re-registered every time user updates
  const companyIdRef = useRef(null);
  const notificationDebounceRef = useRef(null);

  const token =
    Storage.local.get(AUTH_TOKEN_KEY) || Storage.session.get(AUTH_TOKEN_KEY);

  // ─── Initial data fetch ───────────────────────────────────────────────────
  // Bug fix 2026-05-14: previous condition was `pathname !== "/login" || pathname !== "/signup"`
  // which is ALWAYS true (use of || instead of &&). It also didn't check whether
  // a token exists — so any visitor to /homePage, /privacy-policy, /signup, etc.
  // fired getSession() → 401 on whoAmI → noisy console errors on every public-
  // route visit. New gate: skip both probes when there is no auth token. Once a
  // token exists, both fire as before.
  useEffect(() => {
    const token =
      Storage.local.get(AUTH_TOKEN_KEY) || Storage.session.get(AUTH_TOKEN_KEY);
    if (!token) return;
    getSession();
    getAllNotifications();
    // eslint-disable-next-line
  }, []);

  // ─── Language direction setup ─────────────────────────────────────────────
  useEffect(() => {
    const htmlElement = document.getElementsByTagName("html")[0];
    const userLanguage = user?.language || localStorage.getItem(LANGUAGE);
    const isArabic = userLanguage === "ARABIC";
    htmlElement.setAttribute("dir", isArabic ? "rtl" : "ltr");
    setLanguageSelected(isArabic);

    // Sync locale cookie for help.tamamplus.com (cookie sharing across *.tamamplus.com)
    const locale = isArabic ? "ar" : "en";
    document.cookie = `locale=${locale}; path=/; domain=.tamamplus.com; max-age=${60 * 60 * 24 * 365}`;
  }, [token, user?.language]);

  // ─── Scroll behavior ──────────────────────────────────────────────────────
  useEffect(() => {
    if (currentRoute.formScrol) {
      window.showScroll = false;
    } else {
      window.showScroll = true;
    }
  }, [currentRoute.formScrol]);

  // ─── Keep companyIdRef in sync ────────────────────────────────────────────
  // We use a ref so payment listeners always read the latest companyId
  // without needing to be torn down and re-registered on every user update
  useEffect(() => {
    companyIdRef.current = user?.company?.id || null;
  }, [user?.company?.id]);

  // ─── Socket creation ──────────────────────────────────────────────────────
  // Only create once when user + token are available and no socket exists yet.
  // On unmount (logout), disconnect and clear.
  useEffect(() => {
    if (user && !socket && token) {
      const chat = io(import.meta.env.REACT_APP_SERVER_URL_SOCKET, {
        query: { token },
        transports: ["websocket", "polling"],
        reconnection: true,
        // Was 5 (~5s of retries). The post-Moyasar cross-domain reload drops
        // the WebSocket, and on slow Safari iPhone networks the socket needs
        // longer than 5 attempts to reconnect — the queued payment_card_update
        // / payment_status events (held in the backend's pendingSocketEvents
        // queue) are only re-delivered on the join_company emit that fires on
        // reconnect, so a premature give-up left the subscription/payment UI
        // stale until a manual refresh. 20 attempts with a 5s cap gives a
        // ~1-2 min reconnect window. NOT Infinity — that would drain battery
        // and hold a zombie retry loop for genuinely offline/logged-out clients
        // (the unmount cleanup below stops it only on logout).
        reconnectionAttempts: 20,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      });
      setSocket(chat);

      // Cleanup: disconnect socket when component unmounts or user logs out
      return () => {
        chat.disconnect();
        setSocket(null);
      };
    }
    // eslint-disable-next-line
  }, [token, user?.id]);
  // Note: depend on user?.id not the full user object to avoid recreation on every profile update

  // ─── Notification listener ────────────────────────────────────────────────
  // Wait for the socket to be connected before emitting 'notification'
  useEffect(() => {
    if (!socket || !user?.id) return;

    const handleConnect = () => {
      socket.emit("notification", user.id);
    };

    // If already connected, emit immediately; otherwise wait for connect event
    if (socket.connected) {
      socket.emit("notification", user.id);
    } else {
      socket.on("connect", handleConnect);
    }

    socket.on("force-logout", () => {
      clearAuthentication("login.error.emailChanged");
      window.location.href = "/login";
    });

    socket.on("notification-recieve", (payload) => {
      notificationAlert({ ...payload }, currentRole, user);

      // Immediate actions for critical events
      if (payload.event === "roleUpdate") getSession();
      if (payload.event === "languageChanged") {
        localStorage.setItem(LANGUAGE, payload.senderId);
        getSession();
      }

      // Debounce data refresh calls to batch rapid notifications (500ms)
      if (notificationDebounceRef.current) clearTimeout(notificationDebounceRef.current);
      notificationDebounceRef.current = setTimeout(() => {
        getAllNotifications();
        getAdminManagerReports(false);

        if (currentRole === "user") {
          getAssignedFormsForUser({
            limit: 10,
            page: 1,
            sortBy: "formUpdatedAt:desc",
          });
        } else {
          if (window.location.pathname === "/forms/archive") {
            getForms({ sortBy: "updatedAt:desc", isArchived: true });
          } else {
            getForms({
              limit: 10,
              page: 1,
              sortBy: "updatedAt:desc",
              isArchived: false,
            });
          }
        }
      }, 500);
    });

    return () => {
      socket.off("connect", handleConnect);
      socket.off("notification-recieve");
      socket.off("force-logout");
    };
    // eslint-disable-next-line
  }, [socket, currentRole]);

  // ─── Payment socket listeners ─────────────────────────────────────────────
  // This effect registers payment listeners ONCE when the socket is available.
  // It does NOT depend on user?.company?.id directly — instead it reads
  // companyIdRef.current so the handlers always have the latest companyId
  // without needing to be torn down and re-registered on every user update.
  //
  // join_company is emitted inside the 'connect' handler so it runs:
  //   1. On initial connection
  //   2. On every reconnection (socket.io fires 'connect' on reconnect too)
  // This ensures the server always has this client in the correct room
  // and pending events are delivered reliably after reconnects.
  useEffect(() => {
    if (!socket) return;

    const joinCompanyRoom = () => {
      const companyId = companyIdRef.current;
      if (companyId) {
        socket.emit("join_company", companyId);
      }
    };

    // join_company on every connect/reconnect
    socket.on("connect", joinCompanyRoom);

    // If already connected when this effect runs, join immediately
    if (socket.connected) {
      joinCompanyRoom();
    }

    socket.on("payment_status", (data) => {

      const moyasarResponse = data.moyasarResponse || data;
      const lastFour =
        moyasarResponse.source?.number?.slice(-4) ||
        moyasarResponse.source?.last_four ||
        "****";

      // Forward EVERY payment_status event to the in-flow dialog
      // (charge-billing-details.jsx). The dialog filters by currentPaymentId
      // so unrelated cron/renewal events don't transition its state machine.
      // Dispatched BEFORE the toast/notification dedupe check below so the
      // dialog gets the event regardless of whether App.js shows feedback.
      try {
        window.dispatchEvent(new CustomEvent("payment-status", { detail: data }));
      } catch (_) {
        /* CustomEvent unavailable in very old browsers / test contexts */
      }

      // Dedupe gate — if the in-flow Processing/Failed/Success dialog is
      // handling this exact paymentId, suppress App.js's toast +
      // notification-center entry. The dialog IS the feedback. State-refresh
      // calls (getSession + getSubscriptionByCompanyId) still run so the
      // page underneath is fresh by the time the user clicks Close.
      const eventPaymentId =
        (moyasarResponse && moyasarResponse.id) || data.paymentId || null;
      const inFlowPaymentId = (() => {
        try { return sessionStorage.getItem("inFlowPaymentId"); } catch (_) { return null; }
      })();
      const suppressToast =
        eventPaymentId &&
        inFlowPaymentId &&
        String(eventPaymentId) === String(inFlowPaymentId);

      if (
        moyasarResponse.status === "failed" ||
        data.event === "PAYMENT_FAILED" ||
        data.event === "payment_failed"
      ) {
        // Prefer the backend-stamped i18nKey (resolved from Moyasar source.code
        // via utils/moyasarErrorMap.js). Falls back to the raw Moyasar message
        // for unmapped codes, older webhook payloads that don't carry the
        // i18nKey field, OR mid-deploy races where the frontend doesn't yet
        // have the translation key (languageSelector.js returns the
        // "Default text" / "النص الافتراضي" sentinel for unknown keys).
        let errorMsg = moyasarResponse.source?.message || "Payment failed";
        if (data.i18nKey) {
          const translated = i18n(`Subscriptions.${data.i18nKey}`);
          if (
            translated &&
            translated !== "Default text" &&
            translated !== "النص الافتراضي" &&
            translated !== `Subscriptions.${data.i18nKey}`
          ) {
            errorMsg = translated;
          }
        }
        // Payment Failed toast intentionally NOT fired here. The proper
        // surfaces are:
        //   • In-flow: the Payment Failed dialog (charge-billing-details.jsx
        //     state machine + the failedDialogActive sessionStorage flag that
        //     survives the AppRoutes remount cascade).
        //   • Cross-page persistence: the global Footer "Subscription auto-
        //     renewal failed..." banner with RETRY PAYMENT button on every
        //     page when status is Paused.
        // The bottom-corner red toast was redundant with both and was the
        // "decline message keeps appearing alongside the dialog" complaint.
        // The notification bell entry is kept as a persistent record.
        if (!suppressToast) {
          notificationAlert(
            {
              title: "Payment Failed",
              message: `${errorMsg} for card ending in ${lastFour}`,
              type: "error",
            },
            currentRole,
            user
          );
        }

        // Refresh session + subscription on failure too — otherwise paused /
        // rolled-back state (post-refund or post-reverse) stays hidden until
        // the user manually reloads the page.
        getSession();
        const failedCompanyId = companyIdRef.current;
        if (failedCompanyId) {
          getSubscriptionByCompanyId(failedCompanyId);
        }
        // The in-flight charge resolved (failed) — clear the in-flight marker
        // now that the dedupe read above already consumed it. This keeps
        // `inFlowPaymentId` an ACCURATE "a charge is in flight right now" signal:
        // a later FRESH retry then sees null and is allowed to charge, while a
        // remount DURING flight (before this terminal event) still finds the id
        // set and re-attaches without re-charging (the ChargeBillingDetails
        // `initialPaymentId` guard). Done HERE, after the synchronous
        // dispatchEvent + dedupe read, not in the dialog's handler — clearing it
        // there would run before this dedupe read and resurrect the duplicate
        // toast/notification.
        try { sessionStorage.removeItem("inFlowPaymentId"); } catch (_) { /* sessionStorage may be unavailable */ }
        // Defense-in-depth re-charge guard: the in-flow dialog normally clears
        // `processingDialogActive` in its own PAYMENT_FAILED handler, but if the
        // dialog had already unmounted when this event arrived, that handler
        // never ran — leaving the Processing-restore flag set while
        // `inFlowPaymentId` is now null, which would let a remount restore
        // Processing and auto-fire a SECOND /retry-payment. Clearing it here
        // makes the restore fall back to the cron-failure dialog (from
        // subscriptionData.lastPaymentFailure) instead of re-charging.
        try { sessionStorage.removeItem("processingDialogActive"); } catch (_) { /* sessionStorage may be unavailable */ }
      } else if (
        moyasarResponse.status === "paid" ||
        data.event === "PAYMENT_PAID" ||
        data.event === "payment_paid"
      ) {
        const amount = (moyasarResponse.amount / 100).toFixed(2);
        if (!suppressToast) {
          SubscriptionCustomToast(`Payment successful`);
          notificationAlert(
            {
              title: "Payment Successful",
              message: `${amount} SAR paid successfully`,
              type: "success",
            },
            currentRole,
            user
          );
        }

        // Use ref to get companyId — avoids re-registering this listener on user changes
        getSession();
        const companyId = companyIdRef.current;
        if (companyId) {
          getSubscriptionByCompanyId(companyId);
        }
        // The in-flight charge resolved (paid) — clear the in-flight marker
        // after the dedupe read above (same reasoning as the failed branch).
        // The card-update auto-close path already clears this via handleClose;
        // this covers the normal Subscribe-success path too.
        try { sessionStorage.removeItem("inFlowPaymentId"); } catch (_) { /* sessionStorage may be unavailable */ }
        // Defense-in-depth (same as the failed branch): drop the Processing-
        // restore flag in case the dialog had already unmounted and didn't
        // clear it via handleClose/auto-close — the payment is paid, so no
        // restore/retry must survive.
        try { sessionStorage.removeItem("processingDialogActive"); } catch (_) { /* sessionStorage may be unavailable */ }
      }
    });

    socket.on("payment_card_update", async (data) => {

      if (data.type === "personal_card_update") return;

      const isReplacement = data.type === "card_replaced";
      // `needsRetryConfirmation` (new): backend signals the just-added
      // card belongs to a Paused subscription with a fresh
      // `lastPaymentFailure`. Show "Card added — confirm to retry your
      // payment" so the user understands the next step. The frontend
      // then opens an Invoice Preview / charge-billing-details modal
      // where the user explicitly Confirms the retry charge — same
      // code path the manual Retry button uses. The subsequent
      // `payment_status` event delivers the final outcome via the
      // handler above.
      //
      // Intentional NO backward-compat aliasing for `autoRetryTriggered`:
      // old backends with `autoRetryTriggered: true` ALREADY initiated a
      // silent charge in the background. Treating that flag as
      // `needsRetryConfirmation` would open Invoice Preview, and a user
      // Confirm click would create a SECOND Moyasar charge. Old backends
      // talking to this new frontend will fall through to the regular
      // "Card Added Successfully" toast path; the user has no in-UI
      // confirmation modal, but the backend's silent charge still runs
      // and the `payment_status` event delivers the outcome.
      const needsRetryConfirmation = data.needsRetryConfirmation === true;
      if (needsRetryConfirmation) {
        if (sessionStorage.getItem("cardAddToastShown") !== "1") {
          sessionStorage.setItem("cardAddToastShown", "1");
          setTimeout(() => sessionStorage.removeItem("cardAddToastShown"), 30 * 1000);
          SubscriptionCustomToast(
            `💳 ${i18n("Subscriptions.CardAddedConfirmRetry") || "Card added — confirm to retry your payment"}`
          );
        }
      } else if (isReplacement) {
        SubscriptionCustomToast(`💳 Card Updated Successfully`);
      } else {
        // Dedupe with the verifyCard path in company-subscription-view.jsx
        // — that path also fires this toast on Moyasar return (more
        // reliable than the socket since the cross-domain reload often
        // disconnects the WebSocket). 30-second TTL.
        if (sessionStorage.getItem("cardAddToastShown") !== "1") {
          sessionStorage.setItem("cardAddToastShown", "1");
          setTimeout(() => sessionStorage.removeItem("cardAddToastShown"), 30 * 1000);
          SubscriptionCustomToast(`💳 Card Added Successfully`);
        }
      }

      const intent = sessionStorage.getItem("subscriptionIntent");

      if (intent && !isReplacement) {

        try {
          await getSession();
          const companyId = companyIdRef.current;
          if (companyId) {
            await getSubscriptionByCompanyId(companyId);
          }
        } catch (e) {
          console.error("Error refreshing session after card add:", e);
        }
        window.dispatchEvent(new CustomEvent("cardAddedForSubscription"));
      } else if (needsRetryConfirmation && !isReplacement) {
        // No `subscriptionIntent` — user came through the bare Update-
        // Card path (not the Subscribe button) and the subscription is
        // Paused with a fresh failure. The subscriptions page's
        // `paymentRetryAfterCardAdd` listener will open the Invoice
        // Preview where the user Confirms the retry charge. Refresh
        // session + subscription first so the modal renders against
        // current state.
        try {
          await getSession();
          const companyId = companyIdRef.current;
          if (companyId) {
            await getSubscriptionByCompanyId(companyId);
          }
        } catch (e) {
          console.error("Error refreshing session after card add (retry path):", e);
        }
        window.dispatchEvent(new CustomEvent("paymentRetryAfterCardAdd"));
      } else {
        getSession();
      }
    });

    socket.on("payment_card_error", (data) => {
      // Mirror payment_status failure handler: surface the actual Moyasar
      // decline reason instead of a hardcoded "Failed to save card" — this
      // event also fires for charge declines (insufficient funds, issuer
      // declines) when the saved token's subsequent verification fails, not
      // just for genuine "I tried to add a card" flows.
      const moyasarResponse = data?.moyasarResponse || data || {};
      const lastFour =
        moyasarResponse.source?.number?.slice(-4) ||
        moyasarResponse.source?.last_four ||
        "****";
      const errorMsg =
        moyasarResponse.source?.message ||
        moyasarResponse.message ||
        "Card verification failed";

      SubscriptionCustomToast(`❌ ${errorMsg}`);
      notificationAlert(
        {
          title: "Card Verification Failed",
          message: `${errorMsg} for card ending in ${lastFour}`,
          type: "error",
        },
        currentRole,
        user
      );

      // Refresh session + subscription so the rolled-back state surfaces
      // without a manual reload, mirroring payment_status behavior.
      getSession();
      const failedCompanyId = companyIdRef.current;
      if (failedCompanyId) {
        getSubscriptionByCompanyId(failedCompanyId);
      }
    });

    socket.on("subscription_updated", (data) => {
      const companyId = companyIdRef.current;
      if (companyId) {
        getSubscriptionByCompanyId(companyId);
      }
    });

    return () => {
      socket.off("connect", joinCompanyRoom);
      socket.off("payment_status");
      socket.off("payment_card_update");
      socket.off("payment_card_error");
      socket.off("subscription_updated");
    };
    // eslint-disable-next-line
  }, [socket]);
  // Intentionally only depends on socket — payment listeners registered once,
  // companyId read via ref to avoid re-registration loop

  // ─── Role-based theme ─────────────────────────────────────────────────────
  useEffect(() => {
    if (currentRole === "manager" || currentRole === "admin") {
      setShouldReload(true);
    }

    if (currentRole === "user") {
      setTheme("inspector");
    } else {
      setTheme("manager");
    }

    if ((currentRole === "manager" || currentRole === "admin") && shouldReload) {
      setShouldReload(false);
      setTimeout(() => { }, 10);
    } else {
      if (currentRole === "user") {
        setTimeout(() => {
          getSession();
        }, 500);
      }
    }
    // eslint-disable-next-line
  }, [currentRole]);

  return (
    <BrowserRouter>
      <Toaster 
        position="top-center" 
        reverseOrder={false}
        toastOptions={{
          style: {
            direction: languageSelected === "ARABIC" ? "rtl" : "ltr",
            textAlign: languageSelected === "ARABIC" ? "right" : "left",
            display: "flex",
            alignItems: "center",
            justifyContent: languageSelected === "ARABIC" ? "flex-end" : "flex-start",
          },
        }}
      />
      <div
        style={
          languageSelected
            ? { fontFamily: "Noto Sans Arabic" }
            : { fontFamily: "Noto Sans" }
        }
      >
        <AppLayout />
      </div>
    </BrowserRouter>
  );
};

const mapStateToProps = ({ authentication }) => ({
  user: authentication.user,
  currentRoute: authentication.currentRoute,
  currentRole: authentication.currentRole,
});

const mapDispatchToProps = {
  getSession,
  getForms,
  getAllNotifications,
  getAssignedFormsForUser,
  getAdminManagerReports,
  getSubscriptionByCompanyId,
  clearAuthentication,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);